import { useState } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Checkbox, Chip, Stack, Tooltip, Typography } from '@mui/material';

import { roleGroups, roleToRoleInfo, TEAM_OWNER } from './utils/roles';

const sensitiveTooltip =
  'Sensitive data access: this role has API access or can access sensitive user data, such as emails and bank data.';

function SensitiveChip(): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  return (
    <Tooltip
      title={sensitiveTooltip}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      arrow
    >
      <Chip
        // clicking shows the tooltip instead of toggling the role's checkbox
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
        label={
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Sensitive
            <InfoOutlinedIcon fontSize="inherit" />
          </Box>
        }
        size="small"
        color="warning"
      />
    </Tooltip>
  );
}

type RoleEditorProps = {
  selectedRoles: string[];
  onSelectedRolesChange: (selectedRoles: string[]) => void;
};

export default function RoleEditor({ selectedRoles, onSelectedRolesChange }: RoleEditorProps): JSX.Element {
  // Team Owner includes every other role
  const isTeamOwner = selectedRoles.includes(TEAM_OWNER);

  const toggleRole = (role: string) => {
    if (selectedRoles.includes(role)) {
      onSelectedRolesChange(selectedRoles.filter((r) => r !== role));
    } else {
      onSelectedRolesChange([...selectedRoles, role]);
    }
  };

  return (
    <Stack spacing={2}>
      {roleGroups.map((group) => (
        <Box key={group.title}>
          <Typography variant="overline" color="text.secondary">
            {group.title}
          </Typography>
          {group.roles.map((role) => (
            // checkbox, name and description side by side; on mobile the description wraps below the name
            <Box
              key={role}
              component="label"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'auto 1fr', sm: 'auto 300px 1fr' },
                alignItems: 'start',
                columnGap: 1.5,
                py: 1,
              }}
            >
              <Checkbox
                checked={isTeamOwner || selectedRoles.includes(role)}
                disabled={isTeamOwner && role !== TEAM_OWNER}
                onChange={() => toggleRole(role)}
                sx={{ p: 0 }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                <Typography fontWeight={500}>{roleToRoleInfo[role].name}</Typography>
                {roleToRoleInfo[role].sensitive === true && <SensitiveChip />}
              </Box>
              <Box sx={{ gridColumn: { xs: 2, sm: 'auto' } }}>
                <Typography variant="body2" color="text.secondary">
                  {roleToRoleInfo[role].info}
                </Typography>
                {isTeamOwner && role === TEAM_OWNER && (
                  <Typography variant="body2" color="primary">
                    Includes all roles below
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </Stack>
  );
}
